import { DashboardChartEmpty } from '@/pages/dashboard/components/DashboardChartEmpty'
import { DashboardChartHost } from '@/pages/dashboard/components/DashboardChartHost'
import {
	baseChartOptions,
	chartDatetimeRangePadding,
	chartTheme,
	denseDateAxisLabels,
} from '@/pages/dashboard/components/dashboardTheme'
import type { ApexOptions } from 'apexcharts'
import moment from 'moment'
import { useMemo } from 'react'
import ReactApexChart from 'react-apexcharts'

type DailyAreaTrendChartProps = {
	labels: string[]
	buckets: number[]
	dateFrom: string
	seriesName: string
	color?: string
	loading?: boolean
	height?: number
	emptyMessage?: string
	valueFormatter?: (v: number) => string
}

export function DailyAreaTrendChart({
	labels,
	buckets,
	dateFrom,
	seriesName,
	color = '#6366f1',
	loading,
	height = 280,
	emptyMessage = 'No data in this date range.',
	valueFormatter = (v) => `${Math.round(v)}`,
}: DailyAreaTrendChartProps) {
	const hasData = buckets.some((v) => v > 0)
	const dayCount = labels.length
	const tickAmount = dayCount <= 10 ? dayCount : dayCount <= 31 ? 8 : 10
	const crowdedLabels = dayCount > 14
	const total = useMemo(() => buckets.reduce((a, b) => a + b, 0), [buckets])

	const points = useMemo(
		() =>
			labels.map((label, i) => ({
				x: moment(dateFrom).startOf('day').add(i, 'days').valueOf(),
				y: buckets[i] ?? 0,
				label,
			})),
		[labels, buckets, dateFrom]
	)

	const options: ApexOptions = useMemo(
		() => ({
			chart: { ...baseChartOptions, type: 'area', height, animations: { enabled: true, speed: 400 } },
			theme: { mode: 'dark' },
			colors: [color],
			stroke: { curve: 'smooth', width: 2.5 },
			fill: {
				type: 'gradient',
				gradient: { shade: 'dark', type: 'vertical', shadeIntensity: 0.35, opacityFrom: 0.5, opacityTo: 0.05, stops: [0, 85, 100] },
			},
			dataLabels: { enabled: false },
			grid: {
				borderColor: chartTheme.borderColor,
				strokeDashArray: 3,
				padding: { left: 8, right: 16, top: 4, bottom: crowdedLabels ? 4 : 0 },
				xaxis: { lines: { show: false } },
			},
			xaxis: {
				type: 'datetime',
				tickAmount,
				...chartDatetimeRangePadding(points.map((p) => p.x)),
				labels: denseDateAxisLabels(dayCount),
				axisBorder: { show: false },
				axisTicks: { show: false },
			},
			yaxis: {
				min: 0,
				forceNiceScale: true,
				labels: { style: { colors: chartTheme.foreColor, fontSize: '11px' } },
				title: { text: seriesName, style: { color, fontSize: '11px', fontWeight: 600 } },
			},
			tooltip: {
				theme: 'dark',
				x: { format: 'EEE, MMM d, yyyy' },
				y: { formatter: (v) => valueFormatter(v ?? 0) },
			},
			markers: { size: total <= 3 ? 4 : 0, strokeWidth: 0, hover: { size: 6 } },
		}),
		[color, crowdedLabels, dayCount, height, points, seriesName, tickAmount, total, valueFormatter]
	)

	if (loading) {
		return <div className="flex h-[280px] items-center justify-center text-sm text-muted-foreground">Loading chart…</div>
	}

	if (!hasData) {
		return <DashboardChartEmpty message={emptyMessage} />
	}

	return (
		<div className="space-y-3">
			<div className="flex justify-end">
				<span className="inline-flex items-center gap-1.5 rounded-full border border-border/60 bg-secondary/30 px-2.5 py-1 text-[11px] font-medium text-muted-foreground">
					<span className="h-2 w-2 rounded-full" style={{ background: color }} />
					Total
					<span className="text-foreground">{valueFormatter(total)}</span>
				</span>
			</div>
			<DashboardChartHost>
				<ReactApexChart
					options={options}
					series={[{ name: seriesName, data: points.map((p) => ({ x: p.x, y: p.y })) }]}
					type="area"
					height={height}
					width="100%"
				/>
			</DashboardChartHost>
		</div>
	)
}
